///
/// EarthBrowser Admin Dashboard - Permissions API
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from "./authenticatedApi"

export interface PermissionResponse {
  permissions: Array<{
    permission_name: string
    resource: string
    action: string
  }>
  roles: Array<{
    id: number
    name: string
    priority: number
    granted_at: string
    expires_at: string | null
  }>
}

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  status: string
  email_verified: boolean
  created_at: string
  last_login_at: string | null
  last_active_day: string | null
  roles: string[]
  device_count: number
  active_sessions: number
}

export interface UsersResponse {
  users: User[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface SuspendUserRequest {
  reason: string
  duration_days?: number
  suspended_until?: string
}

export interface Role {
  id: number
  name: string
  description: string
  priority: number
  is_system: boolean
  created_at: string
  updated_at: string
  permission_count?: number
}

export interface Permission {
  id: number
  name: string
  resource: string
  action: string
  description: string
  created_at: string
}

export interface RolesResponse {
  roles: Role[]
  total: number
}

export interface PermissionsResponse {
  permissions: Permission[]
  grouped: Record<string, Permission[]>
  total: number
}

export interface AuditLogEntry {
  id: number
  created_at: string
  action: string
  resource_type: string
  resource_id: number
  affected_user_email: string
  actor_email: string
  ip_address: string
  metadata: Record<string, unknown> | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
}

export interface AuditLogResponse {
  logs: AuditLogEntry[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface AuditLogFilters {
  page?: number
  limit?: number
  user_id?: number
  action?: string
  start_date?: string
  end_date?: string
}

export const permissionsApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserPermissions: builder.query<PermissionResponse, void>({
      query: () => "/api/user/permissions",
      providesTags: ["UserProfile"],
    }),

    getAdminUsers: builder.query<UsersResponse, { page?: number; limit?: number; search?: string; status?: string; role?: string; sort?: string; dir?: 'asc' | 'desc' }>({
      query: ({ page = 1, limit = 20, search, status, role, sort, dir }) => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        })
        if (search) params.append("search", search)
        if (status && status !== "all") params.append("status", status)
        if (role && role !== "all") params.append("role", role)
        if (sort) {
          params.append("sort", sort)
          params.append("dir", dir ?? "desc")
        }
        return `/api/admin/users?${params}`
      },
      providesTags: ["UserAccounts"],
    }),

    suspendUser: builder.mutation<{ success: boolean }, { userId: number; body: SuspendUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/api/admin/users/${userId}/suspend`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserAccounts"],
    }),

    unsuspendUser: builder.mutation<{ success: boolean }, number>({
      query: (userId) => ({
        url: `/api/admin/users/${userId}/unsuspend`,
        method: "POST",
      }),
      invalidatesTags: ["UserAccounts"],
    }),

    getRoles: builder.query<RolesResponse, void>({
      query: () => "/api/admin/roles",
      providesTags: ["UserAccounts"],
    }),

    getPermissions: builder.query<PermissionsResponse, void>({
      query: () => "/api/admin/permissions",
    }),

    getAuditLog: builder.query<AuditLogResponse, AuditLogFilters>({
      query: (filters) => {
        const params = new URLSearchParams()
        if (filters.page) params.append("page", String(filters.page))
        if (filters.limit) params.append("limit", String(filters.limit))
        if (filters.user_id) params.append("user_id", String(filters.user_id))
        if (filters.action) params.append("action", filters.action)
        if (filters.start_date) params.append("start_date", filters.start_date)
        if (filters.end_date) params.append("end_date", filters.end_date)
        return `/api/admin/audit-log?${params}`
      },
      providesTags: ["UserAccounts"],
    }),

    grantRole: builder.mutation<{ success: boolean }, { roleId: number; userId: number; expiresAt?: string }>({
      query: ({ roleId, userId, expiresAt }) => ({
        url: `/api/admin/roles/${roleId}/grant`,
        method: "POST",
        body: {
          user_id: userId,
          expires_at: expiresAt,
        },
      }),
      invalidatesTags: ["UserAccounts"],
    }),

    revokeRole: builder.mutation<{ success: boolean }, { roleId: number; userId: number }>({
      query: ({ roleId, userId }) => ({
        url: `/api/admin/roles/${roleId}/revoke`,
        method: "DELETE",
        body: {
          user_id: userId,
        },
      }),
      invalidatesTags: ["UserAccounts"],
    }),
  }),
})

export const {
  useGetUserPermissionsQuery,
  useGetAdminUsersQuery,
  useSuspendUserMutation,
  useUnsuspendUserMutation,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useGetAuditLogQuery,
  useGrantRoleMutation,
  useRevokeRoleMutation,
} = permissionsApi

